// frontend/src/services/upsDriverService.js
// Responsibility: Talk to the backend and return driver-shaped objects. Centralize all UPS API calls + normalization.
// This service wraps UPS driver endpoints—fetches Active/Reserved/Banned (including bulk), normalizes responses to a consistent driver shape, and performs status actions (reserve, activate, ban/unban, update ban).
import axios from 'axios';

/* ---------- helpers ---------- */
const pick = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
  return undefined;
};
const toNum = v => {
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

/** Ensure Active/Reserved rows carry a `status` field and expected props */
const tagStatus = (rows, status) =>
  (Array.isArray(rows) ? rows : (rows?.rows || [])).map(d => {
    const id        = toNum(pick(d.id, d.driver_id, d.driverId, d.driver?.id));
    const agency_id = toNum(pick(d.agency_id, d.agencyId, d.agency?.id));

    return {
      ...d,

      // identity
      id,
      driver_id: id,

      // status
      status,

      // contact / person
      name:  pick(d.name, d.driver_name, d.driver?.name, ''),
      email: pick(d.email, d.driver?.email, ''),
      phone: pick(d.phone, d.driver?.phone, ''),

      // vehicle
      vehicle_registration_number: pick(
        d.vehicle_registration_number,
        d.vehicleRegistrationNumber,
        d.vrn,
        d.vehicle_reg,
        d.driver?.vehicle_registration_number,
        ''
      ),

      // agency
      agency_id,
      agency_name: pick(d.agency_name, d.agencyName, d.agency?.name, ''),

      // dates
      submission_date: pick(d.submission_date, d.created_at, d.submitted_at, d.driver?.submission_date, null),
    };
  });

/**
 * Convert ban records → driver-shaped entities (id = driver_id), keep ban metadata + common fields.
 * Safe for two shapes:
 *  1) Audit rows: { ban_id or id (ban id), driver_id, ... }
 *  2) Driver DTO + ban_id: { id (driver id), ban_id, ... }  <-- returned by our ban/update endpoints
 */
const normalizeBanned = (rows) =>
  (Array.isArray(rows) ? rows : (rows?.rows || [])).map(r => {
    // If server returned a driver DTO (has id but no driver_id) alongside ban_id, treat r.id as driver id.
    const driverIdFromDto = (r && 'ban_id' in r && !('driver_id' in r)) ? r.id : undefined;

    const driver_id = toNum(pick(
      r.driver_id,
      r.driverId,
      r.driver?.id,
      driverIdFromDto // fallback for DTO + ban_id shape
    ));
    const agency_id = toNum(pick(r.agency_id, r.agencyId, r.agency?.id));
    const ban_id    = toNum(pick(r.ban_id, r.id)); // r.id is ban id for audit rows

    return {
      ...r, // raw first

      // identity
      id: driver_id,
      driver_id,

      // status
      status: 'Banned',

      // contact / person
      name:  pick(r.name, r.driver_name, r.driver?.name, ''),
      email: pick(r.email, r.driver?.email, ''),
      phone: pick(r.phone, r.driver?.phone, ''),

      // vehicle
      vehicle_registration_number: pick(
        r.vehicle_registration_number,
        r.vehicleRegistrationNumber,
        r.vrn,
        r.vehicle_reg,
        r.driver?.vehicle_registration_number,
        ''
      ),

      // agency
      agency_id,
      agency_name: pick(r.agency_name, r.agencyName, r.agency?.name, ''),

      // timeline
      submission_date: pick(r.submission_date, r.driver?.submission_date, null),

      // ban metadata
      ban_id,
      ban_reason: pick(r.ban_reason, r.reason, ''),
      banned_at: pick(r.created_at, r.banned_at, null),
    };
  });

/**
 * Fetch UPS drivers by status (Active, Reserved) with optional filters.
 * (Banned uses a different normalizer—see fetchBannedDrivers.)
 * @param {'Active'|'Reserved'|'Banned'} status
 * @param {{agencyId?: number, search?: string}} [opts]
 * @returns {Promise<Driver[]>}
 */
export async function fetchDriversByStatus(status, { agencyId, search } = {}) {
  const params = {};
  if (agencyId) params.agency_id = agencyId;
  if (search)   params.search    = search;

  const { data } = await axios.get(`/ups/drivers/${status}`, { params });

  if (status === 'Banned') {
    // Safety: normalize if someone calls this path for banned
    return normalizeBanned(data);
  }
  return tagStatus(data, status);
}

/**
 * Fetch the UPS audit list of banned drivers with optional filters.
 * Returns driver-shaped entities with ban metadata.
 */
export async function fetchBannedDrivers({ agencyId, search } = {}) {
  const params = {};
  if (agencyId) params.agency_id = agencyId;
  if (search)   params.search    = search;

  const { data } = await axios.get('/ups/drivers/banned', { params });
  return normalizeBanned(data);
}

/**
 * Universal bootstrap: fetch Active + Reserved + Banned in parallel.
 */
export async function fetchAllStatuses({ agencyId, search } = {}) {
  const [active, reserved, banned] = await Promise.all([
    fetchDriversByStatus('Active',   { agencyId, search }),
    fetchDriversByStatus('Reserved', { agencyId, search }),
    fetchBannedDrivers({ agencyId, search }),
  ]);
  return { active, reserved, banned };
}

/** Reserve an active driver → returns a normalized driver ready for upsert */
export async function reserveDriver(id) {
  const { data } = await axios.put(`/ups/drivers/reserved/${id}`);
  const [normalized] = tagStatus([data], 'Reserved');
  return normalized ?? { ...data, status: 'Reserved', id: data.id ?? data.driver_id ?? id };
}

/** Activate a reserved driver → normalized */
export async function activateDriver(id) {
  const { data } = await axios.put(`/ups/drivers/active/${id}`);
  const [normalized] = tagStatus([data], 'Active');
  return normalized ?? { ...data, status: 'Active', id: data.id ?? data.driver_id ?? id };
}

/**
 * Ban a driver → server returns a full driver DTO + ban_id.
 * Handle both shapes defensively.
 */
export async function banDriver(driverId, banReason) {
  const payload = { driver_id: driverId, ban_reason: banReason };
  const { data } = await axios.post('/ups/ban-driver', payload);

  // If it's a driver DTO (id present, no driver_id), tag + keep ban metadata
  if (data && data.id && !('driver_id' in data)) {
    const [normalized] = tagStatus([data], 'Banned');
    return normalized
      ? {
          ...normalized,
          ban_id: data.ban_id ?? normalized.ban_id,
          ban_reason: data.ban_reason ?? normalized.ban_reason,
          banned_at: data.banned_at ?? normalized.banned_at, // <-- keep banned_at
        }
      : { ...data, id: data.id, status: 'Banned', banned_at: data.banned_at, ban_id: data.ban_id };
  }

  // Otherwise treat as ban/audit row
  const [normalized] = normalizeBanned([data]);
  return normalized ?? { id: driverId, status: 'Banned' };
}


/** Update an existing ban record's reason (server returns driver DTO + ban meta) */
export async function updateBan(banId, banReason) {
  const { data } = await axios.put(`/ups/ban-driver/${banId}`, { ban_reason: banReason });

  if (data && data.id && !('driver_id' in data)) {
    const [normalized] = tagStatus([data], 'Banned');
    return normalized ? { ...normalized, ban_id: data.ban_id ?? normalized.ban_id } : null;
  }

  const [normalized] = normalizeBanned([data]);
  return normalized ?? null;
}

/**
 * Unban a driver → backend sets driver back to Reserved (or Active in your rules).
 * Return a normalized driver; default to Reserved if API doesn’t echo fields.
 */
export async function unbanDriver(banIdOrDriverId) {
  const { data } = await axios.put(`/ups/drivers/unban/${banIdOrDriverId}`);
  const status = data?.status ?? 'Reserved';
  const maybeDriver = tagStatus([data], status)[0];
  return maybeDriver ?? {
    ...(data && typeof data === 'object' ? data : {}),
    id: data?.driver_id ?? data?.id ?? toNum(banIdOrDriverId),
    status,
  };
}
